import { createContext, useState, useEffect, useCallback, useContext, ReactNode } from 'react'
import { api } from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import { normalizeAccounts } from '@/services/accounts'
import type { ConnectedAccount } from '@/services/types'

interface AccountContextType {
  accounts: ConnectedAccount[]
  selectedAccount: ConnectedAccount | null
  selectAccount: (id: number) => void
  refreshAccounts: () => Promise<void>
  isLoading: boolean
}

const AccountContext = createContext<AccountContextType | null>(null)

const SELECTED_ACCOUNT_KEY = 'beacon_selected_account_id'

export function AccountProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth()
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([])
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(() => {
    const stored = localStorage.getItem(SELECTED_ACCOUNT_KEY)
    return stored ? Number(stored) : null
  })
  const [isLoading, setIsLoading] = useState(false)

  const refreshAccounts = useCallback(async () => {
    if (!isAuthenticated || !user) return

    setIsLoading(true)
    try {
      const response = await api.getAccounts()
      setAccounts(response.accounts)
    } catch {
      // Fallback: derive from user object for backward compat
      setAccounts(normalizeAccounts(user))
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, user])

  useEffect(() => {
    if (isAuthenticated) {
      refreshAccounts()
    } else {
      setAccounts([])
      setSelectedAccountId(null)
    }
  }, [isAuthenticated, refreshAccounts])

  // Auto-select if only one account, or validate stored selection
  useEffect(() => {
    if (accounts.length === 0) return

    if (accounts.length === 1) {
      setSelectedAccountId(accounts[0].id)
      return
    }

    // If stored selection is no longer valid, clear it
    if (selectedAccountId != null && !accounts.find((a) => a.id === selectedAccountId)) {
      setSelectedAccountId(accounts[0].id)
    }
  }, [accounts, selectedAccountId])

  // Persist selection
  useEffect(() => {
    if (selectedAccountId != null) {
      localStorage.setItem(SELECTED_ACCOUNT_KEY, String(selectedAccountId))
    } else {
      localStorage.removeItem(SELECTED_ACCOUNT_KEY)
    }
  }, [selectedAccountId])

  const selectAccount = (id: number) => {
    setSelectedAccountId(id)
  }

  const selectedAccount = accounts.find((a) => a.id === selectedAccountId) ?? null

  return (
    <AccountContext.Provider
      value={{ accounts, selectedAccount, selectAccount, refreshAccounts, isLoading }}
    >
      {children}
    </AccountContext.Provider>
  )
}

export function useAccounts() {
  const context = useContext(AccountContext)
  if (!context) {
    throw new Error('useAccounts must be used within an AccountProvider')
  }
  return context
}
